import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as d3 from 'd3'
import moment from 'moment'
import { DATE_FORMAT, DATE_FORMAT_API } from 'constants/app'
import { Overlays } from 'utils'
import {
    findDepartureWindows,
    getPeaks,
    transformWindowsForShoal,
    mapDockWindowsToShoalWindows,
} from 'scenarios/calculations'
import { isDualRangePilotScenario } from 'scenarios'

function LineChart(props) {
    const { t } = useTranslation()
    const { loading, error, thresholdMode = 'dock' } = props
    const [datas, setDatas] = useState([])

    let svg,
        linesGroup,
        infosGroup,
        mouseGroup,
        dotsGroups,
        xScale,
        yScale,
        yScaleArea,
        wlo_line,
        wlf_line,
        wlp_line,
        boatDraft_line
    let margin = { top: 20, right: 20, bottom: 50, left: 50 },
        width = props.width - margin.left - margin.right,
        height = props.height - margin.top - margin.bottom

    const format = async () => {
        try {
            const { data } = props
            let parser = d3.timeParse('%Y-%m-%d %H:%M:%S')
            let data_temp = { wlo: [], wlp: [], wlf: [], boatDraft: [] }
            data.forEach(function (d) {
                d.date = parser(moment(d.date).format(DATE_FORMAT_API))
                d.wlo = +d.wlo
                d.wlp = +d.wlp
                d['wlf-spine'] = +d['wlf-spine']
                if (d.wlo !== 0) data_temp['wlo'].push(d)
                if (d.wlp !== 0) data_temp['wlp'].push(d)
                if (d['wlf-spine'] !== 0 && !d.wlo && d.date >= new Date()) data_temp['wlf'].push(d)
                if (props.boatDraft) {
                    let tide =
                        d.wlo !== 0 ? d.wlo : d.wlp >= d['wlf-spine'] ? d['wlf-spine'] : d.wlp
                    if (tide) {
                        let boatDraft = tide - props.boatDraft
                        data_temp['boatDraft'].push({ date: d.date, boatDraft: boatDraft })
                    }
                }
            })

            setDatas(data_temp)
        } catch (e) {
            console.error(e)
        }
    }

    // Use centralized departure window calculation from scenarios module
    const get_seuil_areas = function (value) {
        return findDepartureWindows(datas.boatDraft, value, moment())
    }

    const initScales = () => {
        // Calcule des domains (valeur min max)
        xScale = d3.scaleTime().range([0, width])
        yScale = d3.scaleLinear().range([height, 0])
        yScaleArea = d3.scaleLinear().range([height, 0])

        if (datas.length !== 0) {
            let date_min = d3.min([datas.wlo[0]?.date, datas.wlf[0]?.date, datas.wlp[0]?.date])
            let date_max = d3.max([
                datas.wlo[datas.wlo.length - 1]?.date,
                datas.wlf[datas.wlf.length - 1]?.date,
                datas.wlp[datas.wlp.length - 1]?.date,
            ])
            xScale.domain([date_min, date_max])
            let depth_min = props.dockDepth ? -props.dockDepth : 0
            yScale.domain([
                depth_min,
                d3.max(datas.wlp, function (d) {
                    return Math.max(d.wlo, d.wlp, d['wlf-spine']) + 1.0
                }),
            ])

            if (props.boatDraft)
                yScaleArea.domain([depth_min, d3.max(datas.boatDraft, d => d.boatDraft)])
        }
    }

    const initLines = () => {
        // line WLO
        wlo_line = d3
            .line()
            .curve(d3.curveMonotoneX)
            .x(function (d) {
                return xScale(d.date)
            })
            .y(function (d) {
                return yScale(d.wlo)
            })

        // line WLP
        wlp_line = d3
            .line()
            .curve(d3.curveMonotoneX)
            .x(function (d) {
                return xScale(d.date)
            })
            .y(function (d) {
                return yScale(d.wlp)
            })

        // line WLF-SPINE
        wlf_line = d3
            .line()
            .curve(d3.curveMonotoneX)
            .x(function (d) {
                return xScale(d.date)
            })
            .y(function (d) {
                return yScale(d['wlf-spine'])
            })

        // line BOAT-DRAFT
        if (props.boatDraft) {
            boatDraft_line = d3
                .line()
                .curve(d3.curveMonotoneX)
                .x(function (d) {
                    return xScale(d.date)
                })
                .y(function (d) {
                    return yScale(d['boatDraft'])
                })
        }
    }

    const appendAxes = () => {
        svg.append('g')
            .attr('id', 'xAxis')
            .attr('transform', 'translate(0,' + height + ')')

        svg.append('g')
            .attr('id', 'xAxis-date')
            .attr('transform', 'translate(0,' + (height + 20) + ')')

        svg.append('g').attr('id', 'yAxis')

        svg.append('line').attr('id', 'zero-emphasis').attr('x1', 0).attr('x1', width)

        svg.append('text')
            .attr('class', 'axis-legend')
            .attr('transform', `translate(${width / 2}, ${height + margin.bottom})`)
            .style('text-anchor', 'middle')
            .text(t('general.date_legend'))

        svg.append('text')
            .attr('class', 'axis-legend')
            .attr('transform', 'rotate(-90)')
            .attr('x', -height / 2)
            .attr('y', -margin.left + 10)
            .style('text-anchor', 'middle')
            .text(t('general.tide_legend'))
    }

    const appendElements = () => {
        // Areas-background for texture overlays (renders behind lines)
        svg.append('g').attr('id', 'areas-background')

        linesGroup = svg.append('g').attr('id', 'lines')

        linesGroup.append('line').attr('class', 'line now')
        linesGroup.append('line').attr('class', 'line seuil')
        linesGroup.append('line').attr('class', 'line seuil-shoal')
        linesGroup.append('path').attr('class', 'line wlp')
        linesGroup.append('path').attr('class', 'line wlo')
        linesGroup.append('path').attr('class', 'line wlf')
        linesGroup.append('path').attr('class', 'line draft')

        infosGroup = svg.append('g').attr('id', 'contextual-infos')
        infosGroup.append('rect').attr('class', 'text-elem-bg seuil')
        infosGroup.append('rect').attr('class', 'text-elem-bg dock')
        infosGroup.append('text').attr('class', 'text-elem seuil')
        infosGroup.append('text').attr('class', 'text-elem dock')
        infosGroup.append('g').attr('class', 'intersections')
        infosGroup.append('g').attr('class', 'intersections-shoal')
        infosGroup.append('g').attr('class', 'peaks')

        mouseGroup = svg.append('g').attr('id', 'mouse-over-effects')
        mouseGroup.append('path').attr('class', 'mouse-line')

        dotsGroups = mouseGroup.append('g').attr('id', 'dots')
        dotsGroups.append('circle').attr('class', 'dot wlp')
        dotsGroups.append('circle').attr('class', 'dot wlo')
        dotsGroups.append('circle').attr('class', 'dot wlf')
        dotsGroups.append('circle').attr('class', 'dot draft')

        mouseGroup
            .append('rect')
            .attr('width', width)
            .attr('height', height)
            .attr('fill', 'none')
            .attr('pointer-events', 'all')

        // Main areas at end for click handling
        svg.append('g').attr('id', 'areas')
    }

    const updateLine = () => {
        // Add the wlo path.
        d3.select('.line.wlo').data([datas.wlo]).transition().attr('d', wlo_line)

        // Add the wlp path.
        d3.select('.line.wlp').data([datas.wlp]).transition().attr('d', wlp_line)

        // Add the wlf path.
        d3.select('.line.wlf').data([datas.wlf]).transition().attr('d', wlf_line)

        // Add the wlf path.
        d3.select('.line.now')
            .attr('x1', xScale(moment()))
            .attr('x2', xScale(moment()))
            .attr('y1', 0)
            .attr('y2', height)

        d3.select('.dot.wlo').data([datas.wlo])
        d3.select('.dot.wlp').data([datas.wlp])
        d3.select('.dot.wlf').data([datas.wlf])

        if (props.boatDraft) {
            d3.select('.line.draft').data([datas.boatDraft]).transition().attr('d', boatDraft_line)

            d3.select('.dot.draft').data([datas.boatDraft])
        }
    }

    const updateAxis = () => {
        // Add the X Axis
        d3.select('#xAxis').call(
            d3.axisBottom(xScale).ticks(d3.timeHour.every(3), '%Hh').tickSize(0)
        )

        d3.select('#xAxis-date').call(
            d3.axisBottom(xScale).ticks(d3.timeDay.every(1), '%d-%m').tickSize(0)
        )

        // Add the Y Axis
        d3.select('#yAxis').call(d3.axisLeft(yScale).tickSize(-width))
        d3.select('#zero-emphasis').attr('y1', yScale(0)).attr('y2', yScale(0))
    }

    const updateSeuil = () => {
        let dockThreshold = -props.dockDepth + +props.tolerance
        let shoalThreshold = -props.dockShoal + +props.tolerance
        const hasShoal = props.dockShoal && props.dockShoal > 0

        // Select threshold based on mode (for rendering areas)
        let activeThreshold = thresholdMode === 'shoal' ? shoalThreshold : dockThreshold

        let dockValue_scaled = yScale(dockThreshold)
        let shoalValue_scaled = yScale(shoalThreshold)
        // For area curve bottom - matches whichever areas we're rendering
        let value_scaled = yScale(activeThreshold)
        d3.selectAll('#areas .area').remove()
        d3.selectAll('#areas-background .area').remove()
        d3.selectAll('.area-number').remove()

        // Calculate both area sets
        let dockAreas = get_seuil_areas(dockThreshold)
        let shoalAreasRaw = get_seuil_areas(shoalThreshold)

        // Select which areas to render based on mode
        let areas = thresholdMode === 'shoal' ? shoalAreasRaw : dockAreas
        // For click handler, always pass transformed shoal areas (for pilot calculations)
        let areas_shoal = transformWindowsForShoal(shoalAreasRaw)

        // Dock threshold line - visible in 'dock' or 'both' modes
        d3.select('.line.seuil')
            .transition()
            .attr('x1', 0)
            .attr('y1', dockValue_scaled)
            .attr('x2', width)
            .attr('y2', dockValue_scaled)
            .style('opacity', thresholdMode !== 'shoal' ? 1 : 0)

        // Shoal threshold line - visible in 'shoal' or 'both' modes when shoal exists
        d3.select('.line.seuil-shoal')
            .transition()
            .attr('x1', 0)
            .attr('y1', shoalValue_scaled)
            .attr('x2', width)
            .attr('y2', shoalValue_scaled)
            .style('opacity', thresholdMode !== 'dock' && hasShoal ? 1 : 0)

        let curveFunc = d3
            .area()
            .x(function (d) {
                return xScale(d.date)
            })
            .y1(function (d) {
                return yScale(d.boatDraft)
            })
            .y0(value_scaled)

        // Limit numbered circles to max 4 areas
        const maxNumberedAreas = 4

        // First, render all areas with original styling
        // Filter to only render areas with at least 2 points (required for visible SVG path)
        areas.forEach(function (a, index) {
            // Skip areas with insufficient data points (they would be invisible anyway)
            if (!a || a.length < 2) {
                return
            }

            d3.select('#areas')
                .append('path')
                .attr('class', function (d) {
                    if (!props.startHour) return 'area'
                    const date = moment(props.startHour)
                    const range = [moment(a[0].date), moment(a[a.length - 1].date)]

                    if (date.isBetween(range[0], range[1], undefined, '[]')) return 'area active'
                    return 'area'
                })
                .attr('data-area', new Date(a[a.length - 1].date).getTime())
                .attr('d', curveFunc(a))
                .on('click', async function () {
                    if (props.handleAreaClick) {
                        //if (d3.select(this).classed('active')) return

                        // Guard against empty or malformed area data
                        if (!a || a.length < 2) return

                        const lenght = a.length - 1

                        // Get the data range end time to detect incomplete areas
                        const dataEndTime = d3.max([
                            datas.wlo[datas.wlo.length - 1]?.date,
                            datas.wlf[datas.wlf.length - 1]?.date,
                            datas.wlp[datas.wlp.length - 1]?.date,
                        ])
                        const dataEndTimeMs = dataEndTime ? new Date(dataEndTime).getTime() : 0

                        // Calculate dock-based departure windows starting from clicked area
                        // Include ALL windows (even < 1h) to preserve peak alignment
                        // Mark short windows with windowTooShort flag for email warnings
                        const dockAreasFromClick = dockAreas.slice(index, index + maxNumberedAreas)
                        const dockDepartureWindows = dockAreasFromClick
                            .filter(area => {
                                // Skip incomplete areas (window extends beyond available data)
                                if (
                                    area.interpolatedEnd === null ||
                                    area.interpolatedEnd === undefined
                                )
                                    return false
                                // Also skip areas that end at the data boundary (within 5 minutes)
                                const areaEndTime = new Date(area[area.length - 1].date).getTime()
                                if (
                                    dataEndTimeMs &&
                                    Math.abs(areaEndTime - dataEndTimeMs) < 5 * 60 * 1000
                                )
                                    return false
                                return true
                            })
                            .slice(0, maxNumberedAreas)
                            .map(area => {
                                // Use interpolated crossing times for accurate display
                                // Fall back to data point times if interpolation unavailable
                                const start = area.interpolatedStart || area[0].date
                                const end = area.interpolatedEnd || area[area.length - 1].date
                                const duration = new Date(end) - new Date(start)
                                return {
                                    start: start,
                                    end: end,
                                    windowTooShort: duration < 60 * 60 * 1000,
                                }
                            })

                        // Calculate shoal-based departure windows using extracted function
                        // Maps each dock window to its corresponding shoal window (if any)
                        const shoalDepartureWindows = mapDockWindowsToShoalWindows(
                            dockDepartureWindows,
                            shoalAreasRaw
                        )

                        // Check if this is a pilot scenario (has shoal configured)
                        const isPilotScenario = props.dockShoal && props.dockShoal > 0

                        const status = await props.handleAreaClick(
                            [
                                { date: a[0].date, boatDraft: a[0].boatDraft },
                                { date: a[lenght].date, boatDraft: a[lenght].boatDraft },
                            ],
                            areas_shoal,
                            1, // Selected area is always option 1
                            dockDepartureWindows,
                            shoalDepartureWindows,
                            isPilotScenario
                        )
                        if (!status) return
                        d3.selectAll(`[data-area]`).classed('active', false)
                        d3.selectAll(`[data-area="${d3.select(this).attr('data-area')}"]`).classed(
                            'active',
                            true
                        )
                    }
                })
        })

        // In dock or both mode, render darker edges for pilot-only portions
        if (thresholdMode !== 'shoal' && props.startHour) {
            const selectedTime = new Date(props.startHour).getTime()

            // Find which dock area is selected
            const selectedDockIndex = dockAreas.findIndex(a => {
                const start = new Date(a[0].date).getTime()
                const end = new Date(a[a.length - 1].date).getTime()
                return selectedTime >= start && selectedTime <= end
            })

            if (selectedDockIndex >= 0) {
                const relevantDockAreas = dockAreas.slice(
                    selectedDockIndex,
                    selectedDockIndex + maxNumberedAreas
                )

                // For each dock area, render ONLY the edge portions (pilot-only parts)
                relevantDockAreas.forEach(function (dockArea, idx) {
                    const dockStart = new Date(dockArea[0].date).getTime()
                    const dockEnd = new Date(dockArea[dockArea.length - 1].date).getTime()

                    // Find corresponding shoal area
                    const shoalArea = shoalAreasRaw.find(s => {
                        const sStart = new Date(s[0].date).getTime()
                        const sEnd = new Date(s[s.length - 1].date).getTime()
                        return sStart < dockEnd && sEnd > dockStart
                    })

                    if (shoalArea) {
                        const shoalStart = new Date(shoalArea[0].date).getTime()
                        const shoalEnd = new Date(shoalArea[shoalArea.length - 1].date).getTime()

                        // For selected peak (idx 0), render purple on ENTIRE dock area first
                        // This ensures uniform color, then stripes overlay the edges
                        // Render to areas-background so it's behind lines
                        if (idx === 0) {
                            d3.select('#areas-background')
                                .append('path')
                                .attr('class', 'area area--pilot-selected')
                                .attr('d', curveFunc(dockArea))
                        }

                        // Right edge: dock points after shoal ends (descending tide)
                        const rightEdge = dockArea.filter(
                            d => new Date(d.date).getTime() >= shoalEnd
                        )

                        // Render right edge (descending) for both Scenario 3 and 4
                        if (rightEdge.length > 1) {
                            d3.select('#areas-background')
                                .append('path')
                                .attr('class', 'area area--pilot-extra')
                                .attr('d', curveFunc(rightEdge))
                        }

                        // Left edge: dock points before shoal starts (rising tide)
                        // Only render for Scenario 4 (pilot up/down), not Scenario 3 (pilot desc only)
                        if (isDualRangePilotScenario(props.calculationMethod)) {
                            const leftEdge = dockArea.filter(
                                d => new Date(d.date).getTime() <= shoalStart
                            )
                            if (leftEdge.length > 1) {
                                d3.select('#areas-background')
                                    .append('path')
                                    .attr('class', 'area area--pilot-extra')
                                    .attr('d', curveFunc(leftEdge))
                            }
                        }
                    }
                })
            }
        }

        // Only show numbered circles if a selection has been made
        if (props.startHour) {
            // Always find the selection in dockAreas (the base reference)
            // This ensures consistent numbering regardless of thresholdMode
            const dockSelectedIndex = dockAreas.findIndex(a => {
                const date = moment(props.startHour)
                const range = [moment(a[0].date), moment(a[a.length - 1].date)]
                return date.isBetween(range[0], range[1], undefined, '[]')
            })

            // Get the data range end time to detect incomplete areas
            const dataEndTime = d3.max([
                datas.wlo[datas.wlo.length - 1]?.date,
                datas.wlf[datas.wlf.length - 1]?.date,
                datas.wlp[datas.wlp.length - 1]?.date,
            ])

            // Number areas starting from selected dock area (max 4)
            if (dockSelectedIndex >= 0) {
                const dockAreasFromSelection = dockAreas.slice(
                    dockSelectedIndex,
                    dockSelectedIndex + maxNumberedAreas
                )

                dockAreasFromSelection.forEach(function (dockArea, index) {
                    // Skip if dock window is too short (less than 1 hour)
                    const windowDuration =
                        new Date(dockArea[dockArea.length - 1].date) - new Date(dockArea[0].date)
                    if (windowDuration < 60 * 60 * 1000) return

                    // Skip incomplete areas (window extends beyond available data)
                    if (dockArea.interpolatedEnd === null || dockArea.interpolatedEnd === undefined)
                        return
                    // Also skip areas that end at the data boundary (within 5 minutes)
                    const areaEndTime = new Date(dockArea[dockArea.length - 1].date).getTime()
                    const dataEndTimeMs = dataEndTime ? new Date(dataEndTime).getTime() : 0
                    if (dataEndTimeMs && Math.abs(areaEndTime - dataEndTimeMs) < 5 * 60 * 1000)
                        return

                    // Find the corresponding area in current mode (could be shoal or dock)
                    const dockStart = new Date(dockArea[0].date).getTime()
                    const dockEnd = new Date(dockArea[dockArea.length - 1].date).getTime()
                    const matchingArea = areas.find(a => {
                        const areaStart = new Date(a[0].date).getTime()
                        const areaEnd = new Date(a[a.length - 1].date).getTime()
                        // Check if areas overlap
                        return areaStart < dockEnd && areaEnd > dockStart
                    })

                    // In shoal mode, skip circles for pilot-only areas (no matching shoal)
                    if (!matchingArea) return

                    const peak = matchingArea.reduce(
                        (max, d) => (d.boatDraft > max.boatDraft ? d : max),
                        matchingArea[0]
                    )
                    const numberGroup = d3
                        .select('#areas')
                        .append('g')
                        .attr('class', 'area-number')
                        .attr(
                            'transform',
                            `translate(${xScale(peak.date)}, ${yScale(peak.boatDraft) - 20})`
                        )

                    numberGroup.append('circle').attr('r', 12).attr('class', 'area-number-circle')

                    numberGroup
                        .append('text')
                        .attr('text-anchor', 'middle')
                        .attr('dy', '0.35em')
                        .attr('class', 'area-number-text')
                        .text(index + 1)
                })
            }
        }
    }

    const updateContextualInfos = () => {
        // Peaks
        const peaks = [...getPeaks(datas.wlo, 'wlo'), ...getPeaks(datas.wlf, 'wlf-spine')]

        const d3Peaks = d3.select('.peaks').selectAll('.peak').data(peaks)

        d3Peaks.exit().remove()

        const d3PeaksEnter = d3Peaks
            .enter()
            .append('g')
            .attr('class', 'peak')
            .attr('transform', d => `translate(${xScale(d.date)}, ${yScale(d.value)})`)

        d3PeaksEnter.append('circle').attr('class', d => d.className)

        d3PeaksEnter
            .append('text')
            .text(d => d.value + ' m')
            .attr('class', d => d.className)
            .attr('y', 20)

        d3Peaks
            .transition()
            .attr('transform', d => `translate(${xScale(d.date)}, ${yScale(d.value)})`)

        d3Peaks.select('circle').attr('class', d => d.className)

        d3Peaks
            .select('text')
            .text(d => d.value + ' m')
            .attr('class', d => d.className)

        // Dock depth
        const depth = props.dockDepth ? -props.dockDepth : 0
        if (depth) {
            d3.select('.text-elem.dock')
                .text(`${t('general.dock_depth')} : ${depth} m`)
                .attr('x', 5)
                .attr('y', yScale(depth) - 7)

            let w = d3.select('.text-elem.dock').node().getBoundingClientRect().width
            d3.select('.text-elem-bg.dock')
                .attr('y', yScale(depth) - 22)
                .attr('width', w + 20)
        } else {
            d3.selectAll('.text-elem.dock, .text-elem-bg.dock').attr('x', -1000)
        }

        // Threshold label and intersections
        if (!depth || !props.tolerance) return

        const dockThreshold = depth + +props.tolerance
        const shoalThreshold = props.dockShoal ? -props.dockShoal + +props.tolerance : null
        const hasShoal = props.dockShoal && props.dockShoal > 0

        // Threshold label - shows dock threshold
        d3.select('.text-elem.seuil')
            .text(`${t('general.threshold')} : ${dockThreshold.toFixed(2)} m`)
            .attr('x', 5)
            .attr('y', yScale(dockThreshold) - 7)

        let w = d3.select('.text-elem.seuil').node().getBoundingClientRect().width
        d3.select('.text-elem-bg.seuil')
            .attr('y', yScale(dockThreshold) - 22)
            .attr('width', w + 20)

        const TIME_TICKER_WIDTH = 28

        // Get the data range end time to detect incomplete areas
        const dataEndTime = d3.max([
            datas.wlo[datas.wlo.length - 1]?.date,
            datas.wlf[datas.wlf.length - 1]?.date,
            datas.wlp[datas.wlp.length - 1]?.date,
        ])
        const dataEndTimeMs = dataEndTime ? new Date(dataEndTime).getTime() : 0

        // Check if this is the end marker of an incomplete window (at data boundary)
        const isIncompleteEnd = d => {
            // Only applies to end markers (dir === 'down')
            if (d.dir !== 'down') return false
            const endTimeMs = new Date(d.date).getTime()
            // If end is within 5 minutes of data end, it's incomplete
            return dataEndTimeMs && Math.abs(endTimeMs - dataEndTimeMs) < 5 * 60 * 1000
        }

        // Get text size class based on available space
        const getTextSizeClass = d => {
            // Hide end text for incomplete windows (at data boundary)
            if (isIncompleteEnd(d)) return 'hidden'

            // Calculate width from start->end or end->start reference
            let width
            if (d.end) {
                width = xScale(d.end.date) - xScale(d.date)
            } else if (d.start) {
                width = xScale(d.date) - xScale(d.start.date)
            } else {
                return ''
            }
            if (width < 35) return 'hidden' // Too narrow, hide
            if (width < TIME_TICKER_WIDTH * 2) return 'compact' // Tight, use smaller font
            return '' // Normal
        }

        // Helper to render intersections
        // textAbove: true = position text above the line (for shoal), false = below (for dock)
        const renderIntersections = (selector, threshold, visible, textAbove = false) => {
            const intersections = visible
                ? [].concat(
                      ...get_seuil_areas(threshold).map(area => {
                          // Use interpolated crossing times for accurate display
                          // Fall back to data point times if interpolation unavailable
                          const startDate = area.interpolatedStart || area[0].date
                          const endDate = area.interpolatedEnd || area[area.length - 1].date

                          const start = { ...area[0], date: startDate, dir: 'up' }
                          const end = {
                              ...area[area.length - 1],
                              date: endDate,
                              dir: 'down',
                              start,
                          }
                          start.end = end
                          return [start, end]
                      })
                  )
                : []

            const d3Ints = d3.select(selector).selectAll('.intersection').data(intersections)

            d3Ints.exit().remove()

            // Text positioning: above line (-10) or below line (16)
            const textY = textAbove ? -10 : 16

            // Check if window is narrow (times would overlap)
            const NARROW_THRESHOLD = TIME_TICKER_WIDTH * 2
            const isNarrowWindow = d => {
                if (d.dir === 'up' && d.end) {
                    return xScale(d.end.date) - xScale(d.date) < NARROW_THRESHOLD
                }
                if (d.dir === 'down' && d.start) {
                    return xScale(d.date) - xScale(d.start.date) < NARROW_THRESHOLD
                }
                return false
            }

            // X positioning for individual times
            const getTextX = d => {
                if (textAbove) {
                    return d.dir === 'up' ? -28 : 4 // start left, end right
                }
                return d.dir === 'down' ? -24 : 0 // end left, start right
            }

            // For narrow windows on dock line (bottom): show combined time centered, hide individual times
            const getTextClass = d => {
                if (isIncompleteEnd(d)) return 'hidden'
                // Hide individual times for narrow windows (dock line only)
                if (!textAbove && isNarrowWindow(d)) return 'hidden'
                return textAbove ? '' : getTextSizeClass(d)
            }

            // Combined time text (only for start points of narrow windows on dock line)
            const getCombinedText = d => {
                if (textAbove) return '' // Not for shoal line
                if (d.dir !== 'up' || !d.end || !isNarrowWindow(d)) return ''
                return `${moment(d.date).format('HH:mm')}-${moment(d.end.date).format('HH:mm')}`
            }
            const getCombinedTextClass = d => {
                if (textAbove) return 'hidden' // Not for shoal line
                if (d.dir !== 'up') return 'hidden'
                if (!d.end || !isNarrowWindow(d)) return 'hidden'
                if (isIncompleteEnd(d.end)) return 'hidden'
                return 'combined-time'
            }
            const getCombinedTextX = d => {
                if (!d.end) return 0
                return (xScale(d.end.date) - xScale(d.date)) / 2
            }

            const d3IntsEnter = d3Ints
                .enter()
                .append('g')
                .attr('class', d => {
                    const diff = moment(d.date).diff(moment(props.startHour), 'minutes')
                    if (diff <= 2 && diff >= -2) return 'intersection active'
                    return 'intersection'
                })
                .attr('data-area', d => new Date(d.date).getTime())
                .attr('transform', d => `translate(${xScale(d.date)}, ${yScale(threshold)})`)

            d3IntsEnter.append('rect').attr('class', d => (isIncompleteEnd(d) ? 'hidden' : ''))

            // Individual time text
            d3IntsEnter
                .append('text')
                .attr('class', d => getTextClass(d))
                .text(d => moment(d.date).format('HH:mm'))
                .attr('y', textY)
                .attr('x', d => getTextX(d))

            // Combined time text (for narrow windows, on start points only)
            d3IntsEnter
                .append('text')
                .attr('class', d => getCombinedTextClass(d))
                .text(d => getCombinedText(d))
                .attr('y', textY)
                .attr('x', d => getCombinedTextX(d))
                .style('text-anchor', 'middle')

            d3IntsEnter
                .merge(d3Ints)
                .transition()
                .attr('transform', d => `translate(${xScale(d.date)}, ${yScale(threshold)})`)
                .attr('class', d => {
                    const diff = moment(d.date).diff(moment(props.startHour), 'minutes')
                    if (diff <= 2 && diff >= -2) return 'intersection active'
                    return 'intersection'
                })

            d3Ints.select('rect').attr('class', d => (isIncompleteEnd(d) ? 'hidden' : ''))

            // Update individual time text
            d3Ints
                .select('text:not(.combined-time)')
                .text(d => moment(d.date).format('HH:mm'))
                .attr('y', textY)
                .attr('x', d => getTextX(d))
                .attr('class', d => getTextClass(d))

            // Update combined time text
            d3Ints
                .select('text.combined-time')
                .text(d => getCombinedText(d))
                .attr('y', textY)
                .attr('x', d => getCombinedTextX(d))
                .attr('class', d => getCombinedTextClass(d))
        }

        // Dock intersections - visible in 'dock' or 'both' modes (text below)
        renderIntersections('.intersections', dockThreshold, thresholdMode !== 'shoal', false)

        // Shoal intersections - visible in 'shoal' or 'both' modes when shoal exists (text above)
        renderIntersections(
            '.intersections-shoal',
            shoalThreshold || dockThreshold,
            thresholdMode !== 'dock' && hasShoal,
            true
        )
    }

    const setupInteractions = () => {
        d3.selectAll('#areas, #mouse-over-effects')
            .on('mouseout', () => {
                d3.select('.mouse-line').classed('active', false)
                d3.selectAll('.dot').classed('active', false)
                d3.selectAll('#tooltip').classed('active', false)
            })
            .on('mouseover', () => {
                d3.select('.mouse-line').classed('active', true)
                d3.selectAll('.dot').classed('active', true)
                d3.selectAll('#tooltip').classed('active', true)
            })
            .on('mousemove', e => {
                const [x, y] = d3.pointer(e)
                const xDate = xScale.invert(x)
                const bisect = d3.bisector(d => d.date).left

                const keys = ['wlo', 'wlp', 'wlf', 'boatDraft']

                const infos = { date: xDate }

                keys.forEach((key, i) => {
                    const dataKey = key === 'wlf' ? 'wlf-spine' : key
                    const dataSet = datas[key]

                    const data = dataSet[bisect(dataSet, xDate)]
                    infos[key] = (data ? data[dataKey] : null) || null

                    let translate
                    if (!data || !data[dataKey]) {
                        infos[key] = null
                        translate = `translate(-2000, -2000)`
                    } else {
                        infos[key] = data[dataKey]
                        translate = `translate(${xScale(data.date)}, ${yScale(data[dataKey])})`
                    }

                    if (key === 'boatDraft') key = 'draft'
                    d3.select('.dot.' + key).attr('transform', translate)
                })

                d3.select('.mouse-line').attr('d', () => `M${x},${height} ${x},${0}`)

                if (infos.boatDraft && props.dockDepth)
                    infos.clearance = +props.dockDepth + infos.boatDraft

                updateTooltipContent([x, y], e, infos)
            })
    }

    const updateTooltipContent = ([x, y], event, infos) => {
        const tooltip = d3.select('#tooltip')

        if (event.clientX > window.innerWidth - 230)
            tooltip
                .style('left', event.clientX - 20 - 200 + 'px')
                .style('top', event.clientY - 20 + 'px')
        else
            tooltip.style('left', event.clientX + 20 + 'px').style('top', event.clientY - 20 + 'px')

        const f = v => (v ? parseFloat(v).toFixed(2) : '—')
        tooltip.select('.date-fill').html(moment(infos.date).format(DATE_FORMAT + ' HH:mm'))
        tooltip.select('.wlo-tooltip b').html(f(infos.wlo))
        tooltip.select('.wlf-tooltip b').html(f(infos.wlo ? null : infos.wlf))
        tooltip.select('.wlp-tooltip b').html(f(infos.wlp))
        tooltip.select('.clearance-tooltip b').html(f(infos.clearance))
    }

    const initializeChart = () => {
        // Add pattern definitions for striped areas
        const defs = d3.select('#line-chart').append('defs')
        defs.append('pattern')
            .attr('id', 'stripe-pattern')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 6)
            .attr('height', 6)
            .attr('patternTransform', 'rotate(45)')
            .append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', 0)
            .attr('y2', 6)
            .attr('stroke', '#7b68a4')
            .attr('stroke-width', 3)

        // Pilot-only edge stripes (darker purple)
        defs.append('pattern')
            .attr('id', 'pilot-stripes')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 5)
            .attr('height', 5)
            .attr('patternTransform', 'rotate(45)')
            .append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', 0)
            .attr('y2', 5)
            .attr('stroke', '#6b4c8c')
            .attr('stroke-width', 3)

        svg = d3
            .select('#line-chart')
            .append('g')
            .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')')
        initVar()
        appendAxes()
        appendElements()
    }

    const initVar = () => {
        initScales()
        initLines()
    }

    const updateChart = () => {
        initVar()
        updateLine()
        updateAxis()
        updateContextualInfos()
        setupInteractions()
        if (props.dockDepth && props.tolerance) updateSeuil()
    }

    useEffect(() => {
        if (d3.select('#line-chart').html() === '') initializeChart()
    }, [])

    useEffect(() => {
        if (props.data.length > 0) format()
    }, [props.data, props.boatDraft])

    useEffect(() => {
        if (datas.length !== 0) updateChart()
    }, [datas, props.tolerance, props.dockDepth, props.dockShoal, props.startHour, thresholdMode])

    return (
        <div id='linegraph-print-target' className='line-chart-wrap'>
            <ul className='legend'>
                <li className='wlo-color'>
                    <span />
                    <i>{t('general.wlo')}</i>
                </li>
                <li className='wlf-color'>
                    <span />
                    <i>{t('general.wlf')}</i>
                </li>
                <li className='wlp-color'>
                    <span />
                    <i>{t('general.wlp')}</i>
                </li>
                {props.boatDraft && (
                    <li className='draft-color'>
                        <span />
                        <i>{t('general.boat_draft')}</i>
                    </li>
                )}
                {props.tolerance && props.dockDepth && (
                    <li className='threshold-color'>
                        <span />
                        <i>{t('general.threshold')}</i>
                    </li>
                )}
                {props.tolerance && props.dockShoal > 0 && (
                    <li className='threshold-shoal-color'>
                        <span />
                        <i>{t('general.threshold_shoal')}</i>
                    </li>
                )}
            </ul>

            <div className='line-chart-wrap-overflow-handler'>
                <svg id='line-chart' viewBox={`0 0 ${props.width} ${props.height}`} />
                <Overlays loading={loading} error={error} />
            </div>

            <div id='tooltip'>
                <b className='date-fill'></b>
                <ul>
                    <li className='wlo-tooltip'>
                        {t('general.wlo')} : <b></b>
                    </li>
                    <li className='wlf-tooltip'>
                        {t('general.wlf')} : <b></b>
                    </li>
                    <li className='wlp-tooltip'>
                        {t('general.wlp')} : <b></b>
                    </li>
                    <li className='clearance-tooltip'>
                        {t('general.security_clearance')} : <b></b>
                    </li>
                </ul>
            </div>
        </div>
    )
}

export default LineChart
